import { LocalDate } from '@js-joda/core';
import cx from 'classnames';
import { useInfiniteSearchActiveTeamMembers } from 'components/admin/dashboard/hooks';
import {
  Button,
  ConfirmationModal,
  Form,
  InfiniteScrollSpinner,
  NoResults,
  TableContainer,
} from 'components/admin/ui';
import { useAuthContext } from 'components/auth';
import { RefreshCcw01, UploadCloud01, UserPlus02, Users01 } from 'ethena-icons';
import { useDebounce, useToast } from 'lib/hooks';
import _ from 'lodash';
import { useState } from 'react';
import { PopulationCsvDownload } from '../PopulationCsvDownload';
import { PopulationTable } from './PopulationTable';
import css from './SelectedPopulationTable.module.scss';

interface ISelectedPopulationTableProps {
  className?: string;
  isDisabled?: boolean;
  onAddUserClick: () => void;
  onRemove: (userId: number) => void;
  onResetListClick: () => void;
  onUploadCsv?: (file: File) => void;
  populationName?: string;
  userIds: number[];
  useSearch?: typeof useInfiniteSearchActiveTeamMembers;
}

export const SelectedPopulationTable = ({
  className,
  isDisabled = false,
  onAddUserClick,
  onRemove,
  onResetListClick,
  onUploadCsv,
  populationName = 'Team Members',
  useSearch = useInfiniteSearchActiveTeamMembers,
  userIds,
}: ISelectedPopulationTableProps) => {
  const { message } = useToast();
  const { session } = useAuthContext();
  const isCsvEnabled = !!onUploadCsv;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [
    isResetListConfirmationModalVisible,
    setIsResetListConfirmationModalVisible,
  ] = useState<boolean>(false);

  const debouncedSearchTerm = useDebounce<string>(searchTerm, 250);

  const {
    data: teamMembersSearchResult,
    isValidating,
    scrollRef,
  } = useSearch({
    userIdsFilter: { userIds, exclude: false },
    userSearchTerm: debouncedSearchTerm,
  });

  const teamMembers = _.flatMap(teamMembersSearchResult, (x) => x.results);

  const getFilename = (): string => {
    const alphaNumericOnly = (s: string): string => {
      return _.replace(s, /[^0-9a-z]/gi, '');
    };

    const companyName = alphaNumericOnly(session.company.name);
    const date = LocalDate.now().toString();

    return `${_.chain([
      companyName,
      _.replace(populationName, ' ', ''),
      _.kebabCase(date),
    ])
      .join('_')
      .value()}.csv`;
  };

  const [csvUploadKey, setCsvUploadKey] = useState<number>(0);

  const handleUploadCsv = async (file: File) => {
    if (file) {
      setCsvUploadKey(csvUploadKey + 1);
      onUploadCsv(file);
    }
  };

  return (
    <>
      <TableContainer className={cx(css.tableContainer, className)}>
        <TableContainer.Header className={css.tableHeader}>
          <div>
            <Form.Control
              className={css.search}
              placeholder={`Search ${populationName}`}
              onChange={({ target }) => setSearchTerm(target.value)}
              value={searchTerm}
            />
          </div>
          <div className={css.buttonsContainer}>
            <Button
              className={css.button}
              disabled={isDisabled}
              icon={RefreshCcw01}
              onClick={() => setIsResetListConfirmationModalVisible(true)}
              variant="outline-light"
            >
              Reset list
            </Button>
            {isCsvEnabled && (
              <>
                <Form.SingleFileUpload
                  key={csvUploadKey}
                  accept=".csv"
                  onChange={handleUploadCsv}
                >
                  <Button
                    className={css.button}
                    icon={UploadCloud01}
                    isDisabled={isDisabled}
                    variant="outline-light"
                  >
                    Upload CSV
                  </Button>
                </Form.SingleFileUpload>

                <PopulationCsvDownload.Button
                  mode="default"
                  isDisabled={_.isEmpty(teamMembers)}
                  fallbackFilename={getFilename()}
                  searchParams={{
                    exclude: false,
                    userIds,
                    userSearchTerm: debouncedSearchTerm,
                  }}
                />
              </>
            )}
            <Button
              className={css.button}
              disabled={isDisabled}
              icon={UserPlus02}
              onClick={onAddUserClick}
              variant="outline-light"
            >
              Add {_.toLower(populationName)}
            </Button>
          </div>
        </TableContainer.Header>

        <PopulationTable
          className={css.table}
          onRemove={isDisabled ? undefined : onRemove}
          teamMembers={teamMembers}
        />

        {_.isEmpty(teamMembers) && !isValidating && (
          <NoResults
            icon={Users01}
            title="No Team Members Found"
            message="Adjust your search"
          />
        )}

        <InfiniteScrollSpinner ref={scrollRef} isValidating={isValidating} />
      </TableContainer>

      <ResetListConfirmationModal
        isVisible={isResetListConfirmationModalVisible}
        onClose={() => setIsResetListConfirmationModalVisible(false)}
        onConfirm={() => {
          onResetListClick();
          setIsResetListConfirmationModalVisible(false);
        }}
      />
    </>
  );
};

interface IResetListConfirmationModalProps {
  isVisible: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const ResetListConfirmationModal = ({
  isVisible,
  onClose,
  onConfirm,
}: IResetListConfirmationModalProps) => (
  <ConfirmationModal isVisible={isVisible} onClose={onClose}>
    <ConfirmationModal.Header>
      <ConfirmationModal.Icon color="gray" icon={RefreshCcw01} />
    </ConfirmationModal.Header>
    <ConfirmationModal.Body>
      <ConfirmationModal.Confirmation
        heading="Reset list"
        details="Resetting this list will return it to its original state, removing all changes made since its creation."
      />
    </ConfirmationModal.Body>
    <ConfirmationModal.Footer>
      <Button onClick={onClose} variant="outline">
        Cancel
      </Button>
      <Button onClick={onConfirm}>Reset list</Button>
    </ConfirmationModal.Footer>
  </ConfirmationModal>
);
